import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Wallet,
  XCircle,
  Copy,
  ExternalLink,
  Link,
  Shield,
  Loader2,
  Pencil
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { walletAPI } from '../../services/api';
import { toast } from 'sonner';

const WalletManagement = () => {
  const navigate = useNavigate();
  const { getDisplayName, user } = useAuth();

  // Role / classification
  const isSubInvestor = !!user?.createdBy; // aligned with SecondaryTransfers
  const showCreateWalletAction = !!user && !isSubInvestor; // mirrors showInitiateButton logic

  // UI State
  const [wallets, setWallets] = useState([]);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch & refresh wallets (soft refresh)
  const selectedIdRef = useRef(null);

  const refreshWallets = useCallback(async (preferId) => {
    try {
      setLoading(true);
      const { data } = await walletAPI.getMyWallets();
      const rawWallets = data?.wallets || [];
      const sanitized = rawWallets.map(w => {
        const acctType = w?.bankAccountDetails?.accountType;
        if (acctType) {
          return {
            ...w,
            bankAccountDetails: {
              ...w.bankAccountDetails,
              accountType: acctType.toLowerCase()
            }
          };
        }
        return w;
      });
      setWallets(sanitized);
      let targetId = preferId || selectedIdRef.current;
      if (!targetId && sanitized.length) targetId = sanitized[0]._id;
      const sel = sanitized.find(w => w._id === targetId) || sanitized[0] || null;
      setSelectedWallet(sel);
      selectedIdRef.current = sel?._id || null;
      setError(null);
      return sanitized;
    } catch (err) {
      console.error('Failed to fetch wallet', err);
      setError(err?.response?.data?.message || err.message || 'Failed to load wallet');
      setWallets([]);
      setSelectedWallet(null);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refreshWallets(); }, [refreshWallets]);

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success('Address copied');
  };

  const getStatusBadge = (status) => {
    if (!status) return null;
    const normalized = status.toLowerCase();
    const statusConfig = {
      active: { label: 'Active', class: 'badge-success' },
      inactive: { label: 'Inactive', class: 'badge-error' },
      suspended: { label: 'Suspended', class: 'badge-warning' },
      pending: { label: 'Pending', class: 'badge-warning' }
    };
    const config = statusConfig[normalized] || { label: status, class: 'badge-secondary' };
    return <span className={`badge ${config.class}`}>{config.label}</span>;
  };

  const formatDate = (d) => { try { return d ? new Date(d).toLocaleString() : '—'; } catch { return d || '—'; } };
  const formatNumber = (n) => n === null || n === undefined ? '—' : new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(n);

  return (
    <div className="min-h-screen bg-gray-50">

      <div className="flex flex-col gap-4 px-4 py-6 mx-auto max-w-7xl sm:px-6 lg:px-8 md:flex-row md:items-center md:justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight" style={{ color: '#111827' }}>Wallet Management</h1>
          <p className="mt-1 text-sm text-secondary-600">Manage and review your platform wallets</p>
          {isSubInvestor && (
            <p className="text-xs font-medium text-orange-600">This is a sub-investor account. Wallet creation is restricted. Please contact your custodian.</p>
          )}
        </div>
        {showCreateWalletAction && wallets.length > 0 && wallets.length < 2 && (
          <button 
            onClick={() => setShowCreateModal(true)} 
            
            className="self-start btn btn-primary btn-sm md:self-auto"
            title={`Add ${wallets.some(w => w.bankAccountDetails?.accountType?.toLowerCase() === 'conventional') ? 'Islamic' : 'Conventional'} Wallet`}
          >
            <Link className="w-4 h-4 mr-1" /> Add {wallets.some(w => w.bankAccountDetails?.accountType?.toLowerCase() === 'conventional') ? 'Islamic' : 'Conventional'} Wallet
          </button>
        )}
        {isSubInvestor && wallets.length > 0 && wallets.length < 2 && (
          <button disabled title="Sub-investors cannot create wallets" className="self-start opacity-50 cursor-not-allowed btn btn-primary btn-sm md:self-auto">
            <Link className="w-4 h-4 mr-1" /> Add Wallet
          </button>
        )}
      </div>

      <div className="px-4 py-8 mx-auto space-y-8 max-w-7xl sm:px-6 lg:px-8">
        {wallets.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {wallets.map(w => {
              const active = selectedWallet?._id === w._id;
              const acctType = w.bankAccountDetails?.accountType?.toLowerCase();
              return (
                <button
                  key={w._id}
                  onClick={() => { setSelectedWallet(w); selectedIdRef.current = w._id; }}
                  className={`px-4 py-2 rounded-full text-sm font-medium border transition ${active ? 'bg-primary-600 text-white border-primary-600 shadow-sm' : 'bg-white border-gray-300 text-dark-gray hover:border-primary-400 hover:text-primary-700'}`}
                >
                  {acctType === 'islamic' ? 'Islamic Wallet' : 'Conventional Wallet'}
                </button>
              );
            })}
          </div>
        )}

        {loading && (
          <div className="flex items-center mb-6 space-x-3 text-secondary-600">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Loading wallet...</span>
          </div>
        )}
        {error && !loading && (
          <div className="mb-6 alert alert-error">
            <XCircle className="w-4 h-4" />
            <span>{error}</span>
          </div>
        )}

        {!loading && wallets.length === 0 && !error && (
          <div className="mb-8 card">
            <div className="card-body">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="h-5 w-5 text-warning-600 mt-0.5" />
                <div>
                  <h3 className="mb-1 font-semibold text-secondary-900">No Wallet Found</h3>
                  <p className="mb-3 text-sm text-secondary-600">You don't have any wallets yet. Create your first conventional or islamic wallet.</p>
                  {showCreateWalletAction && (
                    <button 
                      onClick={() => setShowCreateModal(true)} 
                      
                      className="btn btn-primary btn-sm"
                      title="Create Wallet"
                    >
                      <Link className="w-4 h-4 mr-1" /> Create Wallet
                    </button>
                  )}
                  {isSubInvestor && (
                    <button disabled title="Sub-investors cannot create wallets" className="opacity-50 cursor-not-allowed btn btn-primary btn-sm">
                      <Link className="w-4 h-4 mr-1" /> Create Wallet
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {wallets.length > 0 && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2 lg:gap-6">
            {[
              {
                icon: <Wallet className="w-6 h-6 text-primary-600" />,
                iconBg: 'bg-primary-100',
                label: 'Status',
                value: getStatusBadge(selectedWallet?.status || 'active'),
                description: 'Current wallet status'
              },
              {
                icon: null,
                iconBg: 'bg-warning-100',
                label: 'Network',
                value: <span className="font-medium">{selectedWallet?.network || '—'}</span>,
                description: 'Blockchain network'
              }
            ].map((card, idx) => (
              <div key={idx} className="card">
                <div className="card-body">
                  <div className="flex items-center">
                    {card.icon && <div className={`flex-shrink-0 p-3 rounded-lg ${card.iconBg}`}>{card.icon}</div>}
                    <div className={card.icon ? "ml-4 min-w-0 flex-1" : "min-w-0 flex-1"}>
                      <p className="text-sm font-medium truncate text-secondary-600">
                        {card.label}
                      </p>
                      <div className="mt-1 text-2xl font-bold">
                        {card.value}
                      </div>
                      <p className="mt-1 text-xs text-secondary-500">
                        {card.description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {wallets.length > 0 && (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="card">
              <div className="card-header"><div className="flex items-center justify-between"><h3>Wallet Details</h3></div></div>
              <div className="space-y-6 card-body">
                <div>
                  <div className="mb-1 text-xs font-medium text-dark-gray">Wallet Address</div>
                  <div className="flex items-start p-3 space-x-2 rounded-lg bg-secondary-50">
                    <div className="flex-1 font-mono text-xs break-all text-dark-gray">{selectedWallet?.walletAddress}</div>
                    <button onClick={() => selectedWallet?.walletAddress && copyToClipboard(selectedWallet.walletAddress)} className="btn btn-ghost btn-xs" title="Copy Address"><Copy className="w-3 h-3" /></button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><div className="font-semibold text-2xs text-secondary-600">Status</div><div className="mt-1">{getStatusBadge(selectedWallet?.status)}</div></div>
                  <div><div className="font-semibold text-2xs text-secondary-600">Network</div><div className="mt-1 text-sm text-dark-gray">{selectedWallet?.network || '—'}</div></div>
                  <div><div className="font-semibold text-2xs text-secondary-600">Created</div><div className="mt-1 text-sm">{formatDate(selectedWallet?.createdAt)}</div></div>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-header">
                <div className="flex items-center justify-between">
                  <h3>Linked Bank Account</h3>
                  {selectedWallet?.bankAccountDetails && (
                    <button 
                      onClick={() => setShowEditModal(true)} 
                      
                      className="btn btn-ghost btn-xs"
                      title="Edit Bank Account Details"
                    >
                      <Pencil className="w-3 h-3 mr-1" /> Edit
                    </button>
                  )}
                </div>
              </div>
              <div className="space-y-4 card-body">
                {selectedWallet?.bankAccountDetails ? (
                  <>
                    <dl className="grid grid-cols-1 text-sm sm:grid-cols-2 gap-x-8 gap-y-4">
                      {[
                        ['Account Type', selectedWallet.bankAccountDetails.accountType],
                        ['Bank Name', selectedWallet.bankAccountDetails.bankName],
                        ['Account Name', selectedWallet.bankAccountDetails.accountName],
                        ['Account Number', <span className="font-mono text-xs" key="num">{selectedWallet.bankAccountDetails.accountNumber}</span>],
                        ['Category', selectedWallet.bankAccountDetails.accountCategory],
                        selectedWallet.bankAccountDetails.swiftBic && ['SWIFT / BIC', selectedWallet.bankAccountDetails.swiftBic],
                        selectedWallet.bankAccountDetails.branchCode && ['Branch Code', selectedWallet.bankAccountDetails.branchCode]
                      ].filter(Boolean).map(([label, value], i) => (
                        <div key={i} className="flex flex-col">
                          <dt className="font-semibold tracking-wide uppercase text-2xs text-secondary-600">{label}</dt>
                          <dd className="mt-1 break-all text-dark-gray">{value}</dd>
                        </div>
                      ))}
                    </dl>
                    {selectedWallet.bankAccountDetails.shariahComplianceDocument && (
                      <div className="mt-2">
                        <div className="mb-1 font-semibold text-2xs text-secondary-600">Shariah Compliance Document</div>
                        <a href={selectedWallet.bankAccountDetails.shariahComplianceDocument} target="_blank" rel="noreferrer" className="flex items-center text-xs link link-primary">View Document <ExternalLink className="w-3 h-3 ml-1" /></a>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-sm text-secondary-600">No bank account details linked.</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {showCreateModal && showCreateWalletAction && (
        <CreateWalletModal
          onClose={() => setShowCreateModal(false)}
          existingWallets={wallets}
          onCreated={() => {
            // Modal closes after refreshed list (including new wallet) is loaded
            setShowCreateModal(false);
          }}
          onRefresh={(id) => refreshWallets(id)}
          creating={creating}
          setCreating={setCreating}
        />
      )}

      {showEditModal && selectedWallet && (
        <EditBankAccountModal
          wallet={selectedWallet}
          onClose={() => setShowEditModal(false)}
          onUpdated={async () => {
            setShowEditModal(false);
            await refreshWallets(selectedWallet._id);
          }}
        />
      )}
    </div>
  );
};

const EditBankAccountModal = ({ wallet, onClose, onUpdated }) => {
  const [updating, setUpdating] = useState(false);
  const [bankName, setBankName] = useState(wallet.bankAccountDetails?.bankName || '');
  const [accountName, setAccountName] = useState(wallet.bankAccountDetails?.accountName || '');
  const [accountNumber, setAccountNumber] = useState(wallet.bankAccountDetails?.accountNumber || '');
  const [accountCategory, setAccountCategory] = useState(wallet.bankAccountDetails?.accountCategory || 'current');
  const [swiftBic, setSwiftBic] = useState(wallet.bankAccountDetails?.swiftBic || '');
  const [branchCode, setBranchCode] = useState(wallet.bankAccountDetails?.branchCode || '');
  const [shariahDoc, setShariahDoc] = useState(wallet.bankAccountDetails?.shariahComplianceDocument || '');
  const accountType = wallet.bankAccountDetails?.accountType || 'conventional';

  const submit = async (e) => {
    e.preventDefault();
    if (updating) return;
    setUpdating(true);
    try {
      const payload = {
        bankAccountDetails: {
          accountType,
          bankName,
          accountName,
          accountNumber,
          accountCategory,
          swiftBic: swiftBic || undefined,
          branchCode: branchCode || undefined,
          shariahComplianceDocument: shariahDoc || undefined
        }
      };
      const response = await walletAPI.updateWallet(wallet._id, payload);
      toast.success('Bank account updated');
      await onUpdated();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || 'Update failed');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-lg bg-white rounded-lg shadow-lg">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold">Edit Bank Account Details</h3>
          <button className="btn btn-ghost btn-xs" onClick={onClose}><XCircle className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs label-text">Account Type</label>
              <input className="w-full bg-gray-100 input input-bordered input-sm" value={accountType} disabled />
            </div>
            <div>
              <label className="text-xs label-text">Account Category</label>
              <select className="w-full select select-sm" value={accountCategory} onChange={e => setAccountCategory(e.target.value)}>
                <option value="current">current</option>
                <option value="savings">savings</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs label-text">Bank Name</label>
              <select className="w-full select select-sm" value={bankName} onChange={e => setBankName(e.target.value)} required>
                <option value="">Select bank</option>
                <option value="Maybank">Maybank</option>
                <option value="CIMB Bank">CIMB Bank</option>
                <option value="Public Bank">Public Bank</option>
                <option value="RHB Bank">RHB Bank</option>
                <option value="Hong Leong Bank">Hong Leong Bank</option>
                <option value="AmBank">AmBank</option>
                <option value="Bank Islam">Bank Islam</option>
                <option value="Bank Muamalat">Bank Muamalat</option>
                <option value="Affin Bank">Affin Bank</option>
                <option value="Alliance Bank">Alliance Bank</option>
                <option value="Standard Chartered">Standard Chartered</option>
                <option value="HSBC Bank">HSBC Bank</option>
                <option value="OCBC Bank">OCBC Bank</option>
                <option value="UOB Bank">UOB Bank</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="text-xs label-text">Account Name</label>
              <input className="w-full input input-bordered input-sm" value={accountName} onChange={e => setAccountName(e.target.value)} required />
            </div>
          </div>
          <div>
            <label className="text-xs label-text">Account Number</label>
            <input className="w-full input input-bordered input-sm" value={accountNumber} onChange={e => setAccountNumber(e.target.value)} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs label-text">SWIFT / BIC</label>
              <input className="w-full input input-bordered input-sm" value={swiftBic} onChange={e => setSwiftBic(e.target.value)} />
            </div>
            <div>
              <label className="text-xs label-text">Branch Code</label>
              <input className="w-full input input-bordered input-sm" value={branchCode} onChange={e => setBranchCode(e.target.value)} />
            </div>
          </div>
          {accountType === 'islamic' && (
            <div>
              <label className="text-xs label-text">Shariah Compliance Document (URL)</label>
              <input className="w-full input input-bordered input-sm" value={shariahDoc} onChange={e => setShariahDoc(e.target.value)} />
            </div>
          )}
          <div className="flex justify-end pt-2 space-x-2">
            <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} disabled={updating}>Cancel</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={updating}>{updating ? 'Updating...' : 'Update'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CreateWalletModal = ({ onClose, existingWallets, onCreated, onRefresh, creating, setCreating }) => {
  const { user } = useAuth();
  const hasConventional = existingWallets.some(w => w.bankAccountDetails?.accountType?.toLowerCase() === 'conventional');
  const hasIslamic = existingWallets.some(w => w.bankAccountDetails?.accountType?.toLowerCase() === 'islamic');
  const availableTypes = [!hasConventional && 'conventional', !hasIslamic && 'islamic'].filter(Boolean);
  const [accountType, setAccountType] = useState(availableTypes[0] || 'conventional');
  const [bankName, setBankName] = useState('');
  const [accountName, setAccountName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountCategory, setAccountCategory] = useState('current');
  const [swiftBic, setSwiftBic] = useState('');
  const [branchCode, setBranchCode] = useState('');
  const [shariahDoc, setShariahDoc] = useState('');
  const disabled = creating || availableTypes.length === 0;

  const submit = async (e) => {
    e.preventDefault();
    if (disabled) return;
    setCreating(true);
    try {
      const payload = {
        userId: user?._id || user?.id,
        bankAccountDetails: {
          accountType,
          bankName,
          accountName,
          accountNumber,
          accountCategory,
          swiftBic: swiftBic || undefined,
          branchCode: branchCode || undefined,
          shariahComplianceDocument: shariahDoc || undefined
        }
      };
      const { data } = await walletAPI.createWallet(payload);
      const newWallet = data.wallet || data?.data?.wallet;
      if (newWallet) {
        toast.success('Wallet created');
        // Ensure we fetch authoritative wallet list (with newly created wallet) BEFORE closing modal / rendering new pill
        if (onRefresh) await onRefresh(newWallet._id);
        onCreated(newWallet._id);
      } else {
        toast.error('Failed to create wallet');
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-lg bg-white rounded-lg shadow-lg">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold">Create {accountType === 'islamic' ? 'Islamic' : 'Conventional'} Wallet</h3>
          <button className="btn btn-ghost btn-xs" onClick={onClose}><XCircle className="w-4 h-4" /></button>
        </div>
        {availableTypes.length === 0 ? (
          <div className="p-6 text-sm">You already have both conventional and islamic wallets.
            <div className="flex justify-end mt-4"><button className="btn btn-primary btn-sm" onClick={onClose}>Close</button></div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs label-text">Account Type</label>
                <select className="w-full select select-sm" value={accountType} onChange={e => setAccountType(e.target.value)}>
                  {availableTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs label-text">Account Category</label>
                <select className="w-full select select-sm" value={accountCategory} onChange={e => setAccountCategory(e.target.value)}>
                  <option value="current">current</option>
                  <option value="savings">savings</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs label-text">Bank Name</label>
                <select className="w-full select select-sm" value={bankName} onChange={e => setBankName(e.target.value)} required>
                  <option value="">Select bank</option>
                  <option value="Maybank">Maybank</option>
                  <option value="CIMB Bank">CIMB Bank</option>
                  <option value="Public Bank">Public Bank</option>
                  <option value="RHB Bank">RHB Bank</option>
                  <option value="Hong Leong Bank">Hong Leong Bank</option>
                  <option value="AmBank">AmBank</option>
                  <option value="Bank Islam">Bank Islam</option>
                  <option value="Bank Muamalat">Bank Muamalat</option>
                  <option value="Affin Bank">Affin Bank</option>
                  <option value="Alliance Bank">Alliance Bank</option>
                  <option value="Standard Chartered">Standard Chartered</option>
                  <option value="HSBC Bank">HSBC Bank</option>
                  <option value="OCBC Bank">OCBC Bank</option>
                  <option value="UOB Bank">UOB Bank</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="text-xs label-text">Account Name</label>
                <input className="w-full input input-bordered input-sm" value={accountName} onChange={e => setAccountName(e.target.value)} required />
              </div>
            </div>
            <div>
              <label className="text-xs label-text">Account Number</label>
              <input className="w-full input input-bordered input-sm" value={accountNumber} onChange={e => setAccountNumber(e.target.value)} required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-xs label-text">SWIFT / BIC</label><input className="w-full input input-bordered input-sm" value={swiftBic} onChange={e => setSwiftBic(e.target.value)} /></div>
              <div><label className="text-xs label-text">Branch Code</label><input className="w-full input input-bordered input-sm" value={branchCode} onChange={e => setBranchCode(e.target.value)} /></div>
            </div>
            {accountType === 'islamic' && (
              <div>
                <label className="text-xs label-text">Shariah Compliance Document (URL)</label>
                <input className="w-full input input-bordered input-sm" value={shariahDoc} onChange={e => setShariahDoc(e.target.value)} />
              </div>
            )}
            <div className="flex justify-end pt-2 space-x-2">
              <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} disabled={creating}>Cancel</button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={disabled}>{creating ? 'Creating...' : 'Create Wallet'}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default WalletManagement;